/**
 * Pure logic of the user dialog (port of the AddUserComponent form): roles
 * offered, form values <-> user record, validation, and the payload sent.
 */
import { PLATFORM, ROLES, type Platform } from '@/shared/constants';
import { compareCodeUnits } from '@/shared/lib/compare';
import { EMAIL_PATTERN } from '@/shared/lib/validation';
import type { UserInput, UserRecord } from './api';

export interface RoleOption {
  label: string;
  value: string;
}

// Sorted by label; "Admin" is appended (unsorted) for admins.
const BASE_ROLES: RoleOption[] = [
  { label: 'Head Office', value: ROLES.HEAD_OFFICE },
  { label: 'Boutique Manager', value: ROLES.BOUTIQUE_MANAGER },
  { label: 'Sales Associate', value: ROLES.SALES_ASSOCIATE },
  { label: 'Workshop', value: ROLES.WORKSHOP },
  { label: 'Call Center', value: ROLES.CALL_CENTER },
  { label: 'DHL', value: ROLES.DHL },
].sort((a, b) => compareCodeUnits(a.label, b.label));

/**
 * Roles offered in the dialog: admins can also create admins; a user with
 * MTM access can only be an admin or a boutique manager.
 */
export const roleOptions = (
  currentUserRole: string | undefined,
  dmsAccess: boolean,
): RoleOption[] => {
  const roles =
    currentUserRole === ROLES.ADMIN
      ? [...BASE_ROLES, { label: 'Admin', value: ROLES.ADMIN }]
      : BASE_ROLES;
  return dmsAccess
    ? roles.filter((role) =>
        ([ROLES.ADMIN, ROLES.BOUTIQUE_MANAGER] as string[]).includes(
          role.value,
        ),
      )
    : roles;
};

export const MEASUREMENTS_OPTIONS = [
  { label: 'Demi Mesure measurements', value: 'DMS' },
] as const;

export interface UserFormValues {
  username: string;
  email: string;
  language: string;
  role: string;
  workshopIds: string[];
  disabled: boolean;
  abcdiorAccess: boolean;
  dmsAccess: boolean;
  measurements: string;
  villaDior: boolean;
}

/** Defaults of a new user: access to the current platform, role "workshop" when on abcdior. */
export const emptyUserForm = (platform: Platform): UserFormValues => ({
  username: '',
  email: '',
  language: '',
  role: platform === PLATFORM.ABCDIOR ? ROLES.WORKSHOP : '',
  workshopIds: [],
  disabled: false,
  abcdiorAccess: platform === PLATFORM.ABCDIOR,
  dmsAccess: platform === PLATFORM.DMS,
  measurements: '',
  villaDior: false,
});

export const userToForm = (
  user: UserRecord,
  platform: Platform,
): UserFormValues => {
  const defaults = emptyUserForm(platform);
  return {
    username: user.username ?? '',
    email: user.email ?? '',
    language: user.language ?? '',
    role: user.role ?? defaults.role,
    workshopIds: user.workshopIds ?? [],
    disabled: user.disabled ?? false,
    abcdiorAccess: user.abcdiorAccess ?? defaults.abcdiorAccess,
    dmsAccess: user.dmsAccess ?? defaults.dmsAccess,
    measurements: user.dmsSettings?.measurements ?? '',
    villaDior: user.dmsSettings?.villaDior ?? false,
  };
};

export type UserFormErrors = Partial<
  Record<'username' | 'email' | 'language' | 'role', string>
>;

export const validateUserForm = (values: UserFormValues): UserFormErrors => {
  const errors: UserFormErrors = {};
  if (!values.username || values.username.length > 100)
    errors.username = 'Wrong user name';
  if (
    !values.email ||
    values.email.length > 254 ||
    !EMAIL_PATTERN.test(values.email)
  )
    errors.email = 'Wrong email';
  if (!values.language) errors.language = 'Wrong language';
  if (!values.role) errors.role = 'Wrong role';
  return errors;
};

/**
 * The stores of the
 * tree are sent for every role but "workshop", whose workshop filter is
 * kept instead.
 */
export const formToUser = (
  values: UserFormValues,
  assignedStores: readonly string[],
): UserInput => {
  const isWorkshop = values.role === ROLES.WORKSHOP;
  return {
    username: values.username,
    email: values.email,
    language: values.language,
    role: values.role,
    disabled: values.disabled,
    abcdiorAccess: values.abcdiorAccess,
    dmsAccess: values.dmsAccess,
    measurements: values.measurements || null,
    villaDior: values.villaDior,
    workshopIds: isWorkshop ? values.workshopIds : [],
    dmsSettings: {
      measurements: values.measurements || null,
      villaDior: values.villaDior,
    },
    assignedStores: isWorkshop ? [] : [...assignedStores],
  };
};
